import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { count, plural, positions } from '../ui/plural';
import {
  matchName,
  matchedNames,
  rematchNames,
  searchKinds,
  suggestionsFor,
  unmatchName,
  unmatchedNames,
} from '../catalog/partNames';
import type { MatchedName, PartKind, UnmatchedName } from '../catalog/partNames';
import { useMounted } from '../ui/useMounted';

/**
 * Разбор нераспознанных наименований.
 *
 * <p>Приёмщик пишет «фара лев.», импорт приносит «Фара левая перед» из чужой
 * таблицы — и пока написание не сведено к эталону, это три разных товара:
 * искать по складу нечем, категории у карточки нет, а в прайс уезжает
 * написание того, кто заносил.
 *
 * <p><b>Сопоставление правит и прошлое.</b> Разгребают список после импорта,
 * когда все карточки уже заведены; поэтому экран показывает, сколько позиций
 * исправлено — сопоставление, не тронувшее ни одной, владельцу незаметно.
 *
 * <p>Сначала самые ходовые написания: под одним двести карточек, под другим
 * одна, заведённая вчера по ошибке, — и порядок «свежие сверху» после импорта
 * означает «в случайном порядке».
 */
interface Props {
  canManage: boolean;
  onTotalChanged: (total: number) => void;
}

const PAGE = 20;

export function UnmatchedScreen({ canManage, onTotalChanged }: Props) {
  const [names, setNames] = useState<UnmatchedName[]>([]);
  const [total, setTotal] = useState(0);
  const [size, setSize] = useState(PAGE);
  const [openId, setOpenId] = useState<number | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Почему это общий хук, а не ref с эффектом на месте, — в ui/useMounted.ts.
  const mounted = useMounted();

  useEffect(() => {
    void load(size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);

  if (!canManage) {
    return (
      <section className="card">
        <h2>Наименования</h2>
        <p className="note">
          Сводить написания к эталону может владелец или менеджер: одно
          сопоставление меняет вид детали и категорию всех позиций под этим написанием.
        </p>
      </section>
    );
  }

  return (
    <>
    <section className="card">
      <h2>Нераспознанные наименования</h2>

      {error !== null && <p className="note note--error">{error}</p>}
      {done !== null && <p className="note">{done}</p>}

      {loading && names.length === 0 && <p className="note">Загружаем…</p>}

      {!loading && total === 0 && (
        <p className="note">
          Все написания сведены к эталону. Новые появятся здесь сами — приёмку
          несопоставленное название не останавливает.
        </p>
      )}

      {total > 0 && (
        <p className="note">
          Всего {total}. Сначала те, под которыми больше позиций: с них разбор
          и окупается.
        </p>
      )}

      {/*
        * Пересопоставление по нынешнему справочнику.
        *
        * Справочник видов деталей растёт с релизом, а написания клиента
        * заведены раньше: пополнение само по себе не меняет ничего, и
        * владелец продолжает видеть ту же стену нераспознанных. Эндпоинт
        * для этого написан давно, но звать его было некому — дотянуться
        * можно было только повторным импортом, то есть перезалив выгрузку
        * целиком ради пересчёта.
        *
        * Безопасно и потому без подтверждения: сопоставляется только точное
        * совпадение с эталоном или синонимом, а сделанное человеком руками
        * не трогается вовсе.
        */}
      {canManage && total > 0 && (
        <p>
          <button type="button" className="button--ghost" disabled={busy}
                  onClick={() => void rematchAll()}>
            Пересопоставить по справочнику
          </button>
        </p>
      )}

      <ul className="suggestions">
        {names.map((name) => (
          <li key={name.id}>
            <div className="stock-row">
              <div className="stock-info">
                <strong>{name.name}</strong>
                <div className="muted">
                  {name.usageCount === 0
                    ? 'позиций пока нет'
                    : `позиций под этим написанием: ${name.usageCount}`}
                </div>
              </div>
              <div className="stock-action">
                <button
                  type="button"
                  className="button--ghost"
                  onClick={() => setOpenId(openId === name.id ? null : name.id)}
                >
                  {openId === name.id ? 'свернуть' : 'сопоставить'}
                </button>
              </div>
            </div>

            {openId === name.id && (
              <KindPicker
                partName={name}
                onPick={(kind) => void apply(name, kind)}
                onError={setError}
              />
            )}
          </li>
        ))}
      </ul>

      {names.length < total && (
        <button type="button" className="button--ghost" onClick={() => setSize(size + PAGE)}>
          Показать ещё
        </button>
      )}
    </section>

    {/* Снятие ошибочного сопоставления — там же, где его ставят: списком
        ниже разбора, а не отдельной формой в другом месте. */}
    <MatchedNames onUnmatched={() => void load(size)} />
    </>
  );

  async function rematchAll(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await rematchNames();
      // Числом, а не «готово»: «сопоставил» и «ничего не изменилось» —
      // разные новости, и по экрану их иначе не различить.
      if (!mounted.current) return;
      setDone(result.matched === 0
        ? 'Ни одно написание не совпало с эталоном — эти разбираются руками'
        : `Сопоставлено написаний: ${result.matched}, исправлено карточек: ${result.updated}`);
      await load(size);
    } catch (cause) {
      if (mounted.current) {
        setError(cause instanceof ApiError ? cause.message : 'Пересопоставить не удалось');
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  async function load(pageSize: number): Promise<void> {
    setLoading(true);
    try {
      const page = await unmatchedNames(0, pageSize);
      if (mounted.current) {
        setNames(page.items);
        setTotal(page.total);
        onTotalChanged(page.total);
        setError(null);
      }
    } catch (cause) {
      if (mounted.current) setError(describe(cause, 'Список не загрузился'));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }

  async function apply(name: UnmatchedName, kind: PartKind): Promise<void> {
    setError(null);
    try {
      const result = await matchName(name.id, kind.id);
      if (!mounted.current) return;
      setOpenId(null);
      // Число исправленных карточек — это и есть работа экрана. Без него
      // владелец не отличит «сопоставил» от «ничего не произошло».
      setDone(
        `«${name.name}» → «${kind.name}». ${
          result.updated === 0
            ? 'Позиций под этим написанием не было.'
            : `Исправлено карточек: ${result.updated}.`
        }`,
      );
      await load(size);
    } catch (cause) {
      if (mounted.current) setError(describe(cause, 'Сопоставить не удалось'));
    }
  }
}

/**
 * Выбор эталона: сначала подсказки, потом поиск.
 *
 * <p>Подсказки идут по похожести строк и молчат ровно там, где написание
 * дальше всего от эталона: «запаска» не похожа на «Колесо запасное» ничем.
 * Поэтому поиск здесь же, а не отдельной кнопкой.
 */
function KindPicker({
  partName,
  onPick,
  onError,
}: {
  partName: UnmatchedName;
  onPick: (kind: PartKind) => void;
  onError: (message: string) => void;
}) {
  const [suggested, setSuggested] = useState<PartKind[]>([]);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<PartKind[]>([]);
  // Сколько подошло всего: список обрезан, и молчать нельзя —
  // не найдя эталона, разбирающий решит, что его нет вовсе.
  const [foundTotal, setFoundTotal] = useState(0);
  const [asked, setAsked] = useState(false);
  /**
   * Какой эталон ждёт подтверждения.
   *
   * <p>Сопоставление правит вид детали и категорию у всех позиций под
   * написанием разом — у живого клиента это до трёхсот карточек, — и назад
   * не откатывается ничем, кроме восстановления из бэкапа. Название позиции
   * оно не трогает (задача 0304). Фишки эталонов стоят в ряд и похожи
   * друг на друга («Ключ зажигания» рядом с «Замком зажигания»), а
   * разбирают их сотнями подряд: промах мышью стоит трёхсот карточек,
   * утверждающих, что они другая деталь. Поэтому второе нажатие — так же,
   * как у правки списком и у отказа по заказу.
   */
  const [confirming, setConfirming] = useState<number | null>(null);
  // Почему это общий хук, а не ref с эффектом на месте, — в ui/useMounted.ts.
  const mounted = useMounted();

  useEffect(() => {
    void suggestionsFor(partName.id)
      .then((found) => { if (mounted.current) setSuggested(found); })
      .catch(() => { if (mounted.current) setSuggested([]); })
      .finally(() => { if (mounted.current) setAsked(true); });
  }, [partName.id, mounted]);

  return (
    <div className="picker">
      {partName.sampleTitle !== null && (
        <p className="note">
          Сейчас: <b>{partName.sampleTitle}</b>
        </p>
      )}

      {suggested.length > 0 && (
        <>
          <div className="muted">Похожие эталоны</div>
          <div className="chips">
            {suggested.map((kind) => (
              <button
                key={kind.id}
                type="button"
                className="chip"
                onClick={() => pick(kind)}
              >
                {label(kind)}
              </button>
            ))}
          </div>
        </>
      )}

      {asked && suggested.length === 0 && (
        <p className="note">
          Похожего в справочнике нет — найдите по смыслу. «Запаска» называется
          «Запасное колесо», и по буквам это не ищется.
        </p>
      )}

      <label>
        Найти эталон
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            void lookup(e.target.value);
          }}
          placeholder="фара, бампер, стойка"
          autoCapitalize="none"
        />
      </label>

      {found.length > 0 && (
        <>
          <div className="chips">
            {found.map((kind) => (
              <button
                key={kind.id}
                type="button"
                className="chip"
                onClick={() => pick(kind)}
              >
                {label(kind)}
              </button>
            ))}
          </div>
          {/* Список обрезан — говорим об этом: не найдя нужного среди
              двух десятков, разбирающий решит, что эталона нет вовсе,
              и оставит написание неразобранным либо возьмёт похожий,
              а одно сопоставление правит сотни карточек. */}
          {foundTotal > found.length && (
            <p className="note note--error">
              Подошло {foundTotal}, показаны первые {found.length} — уточните запрос.
            </p>
          )}
        </>
      )}

      {query.trim().length >= 2 && found.length === 0 && (
        <p className="note">
          Ничего не нашлось. Справочник видов деталей общий и пополняется
          с релизом — своих эталонов в нём не завести.
        </p>
      )}
    </div>
  );

  /**
   * Первое нажатие спрашивает, второе применяет.
   *
   * <p>Подтверждение живёт на самой фишке, а не в отдельном окне.
   */
  function pick(kind: PartKind): void {
    if (confirming !== kind.id) {
      setConfirming(kind.id);
      return;
    }
    setConfirming(null);
    onPick(kind);
  }

  function label(kind: PartKind): string {
    if (confirming !== kind.id) {
      return kind.name;
    }
    return partName.usageCount > 0
      ? `Точно? ${positions(partName.usageCount)}`
      : 'Точно?';
  }

  async function lookup(term: string): Promise<void> {
    if (term.trim().length < 2) {
      setFound([]);
      return;
    }
    try {
      const page = await searchKinds(term.trim());
      if (mounted.current) {
        setFound(page.items);
        setFoundTotal(page.total);
      }
    } catch (cause) {
      if (mounted.current) onError(describe(cause, 'Поиск эталона не работает'));
    }
  }
}

/**
 * Сопоставленные написания — и снятие ошибочного сопоставления (задача 0167).
 *
 * <p>`POST /api/part-names/{id}/unmatch` написан с самого начала и не
 * вызывался ни одним экраном: разбор показывал **только** нераспознанные,
 * то есть сведённое с эталоном человеку не показывалось вовсе. Сопоставить
 * он мог, отменить — нет, а после переезда клиента таких сопоставлений
 * сотни, и ошибочное означает деталь, уехавшую в объявление под чужим
 * наименованием: покупатель ищет «фару», а позиция лежит под «фонарём».
 *
 * <p>В строке видно всё, что снимаешь: написание, эталон **словом**, число
 * позиций под написанием и образец заголовка. Снятие, после которого
 * непонятно, что изменилось, вернёт человека к разработчику — то же
 * правило, по которому сопоставление рядом отвечает числом исправленных
 * карточек.
 */
function MatchedNames({ onUnmatched }: { onUnmatched: () => void }) {
  const [items, setItems] = useState<MatchedName[]>([]);
  const [total, setTotal] = useState(0);
  const [size, setSize] = useState(PAGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  /**
   * Какое написание ждёт подтверждения.
   *
   * <p>Второе нажатие — то же решение, что у сопоставления рядом: строки
   * стоят в ряд и разбирают их подряд, а промах мышью уводит написание
   * из этого списка в стену нераспознанных, где его потом искать среди
   * шестисот.
   */
  const [confirming, setConfirming] = useState<number | null>(null);
  // Почему это общий хук, а не ref с эффектом на месте, — в ui/useMounted.ts.
  const mounted = useMounted();

  useEffect(() => {
    void load(size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);

  return (
    <section className="card">
      <h2>Сопоставленные написания</h2>

      {error !== null && <p className="note note--error">{error}</p>}
      {done !== null && <p className="note">{done}</p>}

      {/* «Загружаем…» — пока грузим, а не пока пусто: иначе надпись висит
          рядом с красной причиной отказа. */}
      {loading && items.length === 0 && error === null && (
        <p className="note">Загружаем…</p>
      )}

      {!loading && error === null && total === 0 && (
        <p className="note">
          Сведённых с эталоном написаний пока нет — они появятся здесь, как
          только вы сопоставите первое.
        </p>
      )}

      {total > 0 && (
        <>
          <p className="note">
            Сведено с эталоном {count(total)}{' '}
            {plural(total, 'написание', 'написания', 'написаний')}
            {items.length < total && ` — показаны первые ${count(items.length)}`}.
          </p>
          {/* Что делает снятие — сказано до нажатия, а не после. Это видимое
              человеку поведение: карточки не меняются вовсе, потому что
              снятие не откатывает вид детали и категорию. */}
          <p className="note">
            Снятие возвращает написание в список нераспознанных. Позиции под
            ним остаются как есть: вид детали и категория у них те, что
            поставило сопоставление, — назад это не откатывается. Название
            позиции сопоставление не меняет.
          </p>
        </>
      )}

      <ul className="suggestions">
        {items.map((row) => (
          <li key={row.partName.id}>
            <div className="stock-row">
              <div className="stock-info">
                <strong>{row.partName.name}</strong>
                <div className="muted">
                  {/* Эталон словом, а не номером: «снять сопоставление»
                      без названия означает «снять неизвестно что». */}
                  эталон: {row.kindName ?? 'нет в справочнике'}
                  {' · '}
                  {row.partName.usageCount === 0
                    ? 'позиций пока нет'
                    : `позиций под этим написанием: ${row.partName.usageCount}`}
                </div>
                {row.partName.sampleTitle !== null && (
                  <div className="muted">Сейчас: {row.partName.sampleTitle}</div>
                )}
              </div>
              <div className="stock-action">
                <button
                  type="button"
                  className="button--ghost"
                  onClick={() => void drop(row)}
                >
                  {confirming === row.partName.id
                    ? confirmLabel(row)
                    : 'Снять сопоставление'}
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {items.length < total && (
        <button type="button" className="button--ghost" onClick={() => setSize(size + PAGE)}>
          Показать ещё
        </button>
      )}
    </section>
  );

  /** Цена действия числом — как «Точно? 40 позиций» у сопоставления рядом. */
  function confirmLabel(row: MatchedName): string {
    return row.partName.usageCount > 0
      ? `Точно снять? ${positions(row.partName.usageCount)} не тронем`
      : 'Точно снять?';
  }

  async function load(pageSize: number): Promise<void> {
    setLoading(true);
    try {
      const page = await matchedNames(0, pageSize);
      if (mounted.current) {
        // Ответ — обещание, а не гарантия: на пустом теле экран упал бы
        // целиком, а у арендатора первого дня оно ровно такое.
        setItems(page.items ?? []);
        setTotal(page.total ?? 0);
        setError(null);
      }
    } catch (cause) {
      if (mounted.current) setError(describe(cause, 'Список не загрузился'));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }

  /** Первое нажатие спрашивает, второе снимает. */
  async function drop(row: MatchedName): Promise<void> {
    if (confirming !== row.partName.id) {
      setConfirming(row.partName.id);
      return;
    }
    setConfirming(null);
    setError(null);
    try {
      await unmatchName(row.partName.id);
      if (!mounted.current) return;
      // Словами, а не «готово»: человеку надо знать и что написание вернулось
      // в разбор, и что карточки при этом не тронуты.
      setDone(
        `«${row.partName.name}» больше не сведено с «${row.kindName ?? 'эталоном'}»`
        + ' — написание вернулось в нераспознанные.'
        + (row.partName.usageCount > 0
          ? ` Карточки не тронуты: ${count(row.partName.usageCount)} `
            + `${plural(row.partName.usageCount, 'позиция', 'позиции', 'позиций')}.`
          : ''),
      );
      await load(size);
      // Снятое вернулось в нераспознанные: их список и счётчик на вкладке
      // обязаны это показать, иначе написание исчезает с экрана вовсе.
      if (mounted.current) onUnmatched();
    } catch (cause) {
      if (mounted.current) setError(describe(cause, 'Снять сопоставление не удалось'));
    }
  }
}

function describe(cause: unknown, fallback: string): string {
  if (cause instanceof ApiError) {
    if (cause.status === 0) {
      return 'Нет связи с сервером. Ничего не изменилось — повторите.';
    }
    if (cause.status === 403) {
      return 'Сводить написания к эталону может владелец или менеджер';
    }
    return cause.message;
  }
  return fallback;
}
